import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Dialog } from '../../../../@core/services/modal/modal.interfaces';
import { DialogButtonsDirective } from '../../../../shared/components/modal-dialog/dialog-buttons.directive';
import { Hotkey } from '../../model/hotkey.model';
import { ShortcutInputComponent } from '../shortcut-input/shortcut-input.component';
import { ShortcutRegistryService } from '../../services/shortcut-registry.service';

@Component({
  selector: 'app-shortcut-record-dialog',
  templateUrl: './shortcut-record-dialog.component.html',
  styleUrls: ['./shortcut-record-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    TranslatePipe,
    ButtonModule,
    InputTextModule,
    DialogButtonsDirective,
    ShortcutInputComponent,
  ]
})
export class ShortcutRecordDialogComponent implements Dialog<string[] | null> {
  resolveWith!: (result?: string[] | null) => void;

  buttonId = signal<string>('');
  currentCombos = signal<string[]>([]);

  filteredCurrentCombos = computed(() => Hotkey.filterCombosForDisplay(this.currentCombos()));
  combosInput = signal<string[]>([]);
  hasError = signal<boolean>(false);
  hasContent = signal<boolean>(false);

  @ViewChild(ShortcutInputComponent) shortcutInputComponent!: ShortcutInputComponent;
  readonly shortcutRegistry = inject(ShortcutRegistryService);

  /** Reject non-platform combos on Windows (allows all on Mac). */
  platformComboFilter = (combo: string): boolean => Hotkey.filterCombosForDisplay([combo]).length > 0;

  constructor() {
    effect(() => {
      const curr = this.currentCombos();
      if (curr?.length) {
        this.combosInput.set([...curr]);
        this.hasContent.set(true);
      }
    });
  }

  onCombosChange(combos: string[]): void {
    this.combosInput.set(combos);
  }

  clear(): void {
    this.resolveWith(null);
  }

  save(): void {
    // Auto-add preview combo if user typed one but didn't click Add
    this.shortcutInputComponent?.addCurrentCombo();
    if (this.hasError()) return;

    // Read from child as source of truth (avoids parent-child sync delay)
    const combos = this.shortcutInputComponent
      ? this.shortcutInputComponent.getCombosForSave()
      : this.combosInput()
          .map((c) => c.trim().toLowerCase())
          .filter(Boolean);

    if (combos.length === 0) {
      this.resolveWith(null);
      return;
    }

    for (const normalized of combos) {
      const isSequence = !normalized.includes('+');
      if (!isSequence) {
        const parts = normalized.split('+');
        if (parts.length === 1 && parts[0].length === 1) {
          this.shortcutInputComponent?.setValidationError(_('hotkeys.shortcut-single-key'));
          return;
        }
      }
    }
    // Conflicts (other_button/reserved) are allowed - setShortcut will overwrite

    this.shortcutInputComponent?.setValidationError(null);
    const full = this.currentCombos();
    const hidden = full.filter(c => !Hotkey.filterCombosForDisplay(full).includes(c));
    this.resolveWith([...hidden, ...combos]);
  }

  cancel(): void {
    this.resolveWith(undefined as any);
  }
}
